// Quantum US Capital Partners — public marketing copy.
// Phase 6 language surgery: no "investment sponsor" (except Terms), no
// "focused on / delivering value / creating opportunities / ecosystem /
// platform / intersection / differentiated / best-in-class" in body copy.

export const HOME = {
  hero: {
    eyebrow: "Quantum US Capital Partners",
    headline: ["Investing with Precision.", "Building Enduring Value."],
    ctas: [
      { label: "Contact Us", to: "/contact", variant: "primary" },
      { label: "Our Strategy", to: "/investment-strategy", variant: "secondary" },
    ],
  },
  whoWeAre: {
    eyebrow: "Who We Are",
    heading: "An independent firm built to endure.",
    paragraph:
      "A privately held, partner-led investment institution.",
    paragraph2:
      "We acquire and steward strategic businesses across cycles rather than around them.",
  },
  investmentFocus: {
    eyebrow: "Investment Focus",
    heading: "Where We Invest.",
    items: [
      "Industrial Manufacturing",
      "Energy",
      "Infrastructure",
      "Private Equity",
      "Special Situations",
      "Cross-Border Investments",
      "Strategic Partnerships",
      "Family-Owned Businesses",
    ],
  },
  philosophy: {
    eyebrow: "Investment Philosophy",
    heading: "A discipline before it is a strategy.",
    lines: [
      "Capital should be patient.",
      "Decisions should be rational.",
      "Execution should be considered.",
      "Relationships should last generations.",
    ],
  },
  philosophyMoment: {
    eyebrow: "Philosophy",
    lines: ["Capital is abundant.", "Judgment is rare."],
  },
  closing: {
    name: "Quantum US Capital Partners LLC",
    eyebrow: "The Art of Capital",
  },
};

export const FIRM = {
  hero: { eyebrow: "The Firm", headline: "Built for the Long Term." },
  intro:
    "A privately held firm. We acquire, develop, and steward businesses in industries we understand deeply.",
  mission: {
    eyebrow: "Mission",
    heading: "Mission.",
    body:
      "Allocate capital with judgment. Partner with capable management. Build businesses that endure.",
  },
  vision: {
    eyebrow: "Vision",
    heading: "Vision.",
    body:
      "A globally respected investment institution known for judgment, integrity, and responsible stewardship of capital.",
  },
  values: {
    eyebrow: "Values",
    heading: "Principles that govern our work.",
    items: [
      { name: "Integrity", body: "Conduct that holds up under scrutiny." },
      { name: "Stewardship", body: "Capital is entrusted, not owned outright." },
      { name: "Long-term Thinking", body: "Outcomes measured in cycles, not quarters." },
      { name: "Operational Excellence", body: "Returns earned in the business, not extracted." },
      { name: "Confidentiality", body: "Discretion is part of the work." },
      { name: "Alignment of Interests", body: "Our capital sits alongside our partners'." },
    ],
  },
  closing: "Capital, applied with judgment.",
};

export const STRATEGY = {
  hero: { eyebrow: "Investment Strategy", headline: "Capital with Conviction. Operational depth." },
  overview: {
    heading: "A patient, operationally-led approach.",
    body:
      "We invest where patience and operational depth compound across cycles.",
  },
  targetAreas: {
    eyebrow: "Target Areas",
    heading: "Where we focus.",
    items: [
      { name: "Private Equity", body: "Control positions in businesses with durable paths to improvement." },
      { name: "Industrial Assets", body: "Hard-asset businesses compounded through operational rigour." },
      { name: "Infrastructure", body: "Long-life assets underpinning essential systems." },
      { name: "Energy", body: "Assets with credible economics and resilient demand." },
      { name: "Manufacturing", body: "Specialist producers in technical end-markets." },
      { name: "Technology-Enabled Industry", body: "Where software deepens the advantage, not replaces it." },
      { name: "Cross-Border Transactions", body: "Structural fluency across jurisdictions and regulators." },
      { name: "Family-Owned Businesses", body: "Succession transitions for owners seeking continuity." },
    ],
  },
  characteristics: {
    eyebrow: "Investment Characteristics",
    heading: "What we look for.",
    items: [
      "Long-term orientation",
      "Operational enhancement",
      "Strong management teams",
      "Scalable businesses",
      "Strategic industries",
      "Responsible governance",
    ],
  },
  interlude: { quote: "We do not invest in trends. We invest in durability." },
};

export const LEADERSHIP = {
  hero: { eyebrow: "Leadership", headline: "Experience earned across decades and cycles." },
  intro:
    "A small group of investors and operators. Long-term partnership over visibility. Capital with Judgment.",
  leaders: [
    {
      initials: "Q.C.",
      title: "Founder & Managing Partner",
      bio: "Responsible for the firm's strategic direction, investment activities and capital allocation. Brings experience across industrial manufacturing, energy, infrastructure, private equity and cross-border investments.",
    },
    {
      initials: "M.P.",
      title: "Managing Partner",
      bio: "Oversees portfolio governance and investment review. A career spanning private equity, cross-border M&A, and comparable investment programmes.",
    },
    {
      initials: "A.J.",
      title: "Partner — Industrials & Manufacturing",
      bio: "Leads industrial and specialist-manufacturing investments. Combines operating leadership with investment responsibility for comparable businesses.",
    },
    {
      initials: "S.R.",
      title: "Partner — Infrastructure & Energy",
      bio: "Leads infrastructure and energy investments across regulated assets, long-duration concessions, and businesses in both conventional generation and the energy transition.",
    },
  ],
  closing: "Discretion is part of the work.",
};

export const GLOBAL = {
  hero: { eyebrow: "Global Presence", headline: "A focused international footprint." },
  intro:
    "Headquartered in the United States. Strategic presence in Europe and the Middle East.",
  locations: [
    { eyebrow: "Headquarters", name: "United States", body: "Capital committed with judgment. Governance. Partnership origination." },
    { eyebrow: "Strategic Presence", name: "Luxembourg", body: "European structuring and cross-border coordination." },
    { eyebrow: "Strategic Presence", name: "Dubai", body: "Middle Eastern partnerships and regional access." },
  ],
  regions: [
    { eyebrow: "North America", body: "Core market for industrial and infrastructure investing." },
    { eyebrow: "Europe", body: "Structuring, counterparty relationships, and selective investments." },
    { eyebrow: "Middle East", body: "Long-term partnerships and capital relationships." },
  ],
};

export const INVESTOR_PORTAL = {
  hero: { eyebrow: "Investor Portal", headline: "Access by Invitation." },
  paragraph:
    "A private access channel for existing partners and institutional counterparties. Access is provided upon verified engagement.",
  reassurance:
    "Reporting, capital documentation and portfolio correspondence are provisioned individually. For credentials or to inquire about partnership, please contact the Office of the Chairman.",
  cta: { label: "Request Access", href: "/contact?subject=Investor%20Portal%20%E2%80%94%20Request%20Access" },
  contactEmail: "chairman@quscap.com",
};

export const CAREERS = {
  hero: { eyebrow: "Careers", headline: "Selection by invitation." },
  paragraph:
    "The firm expands deliberately. Senior appointments are made through direct partnership and long-standing professional relationships rather than through open recruitment. Expressions of interest from investment professionals, operators, and advisers are read personally.",
  intro: "To register interest, please write to:",
  contactEmail: "chairman@quscap.com",
};

export const INSIGHTS_META = {
  hero: {
    eyebrow: "Quantum Letters",
    headline: "Quantum Letters",
    subtitle: "On Capital. Judgment. The Long Term.",
  },
};

export const MAP_MARKERS = [
  { id: "us-hq", name: "United States", role: "Headquarters", coordinates: [-74.006, 40.7128], kind: "hq" },
  { id: "lu", name: "Luxembourg", role: "Strategic Presence", coordinates: [6.1319, 49.6116], kind: "presence" },
  { id: "ae", name: "Dubai, UAE", role: "Strategic Presence", coordinates: [55.2708, 25.2048], kind: "presence" },
];

export const MAP_ARCS = [
  { id: "us-lu", from: [-74.006, 40.7128], to: [6.1319, 49.6116] },
  { id: "us-ae", from: [-74.006, 40.7128], to: [55.2708, 25.2048] },
];
